import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { TrendingUp, Loader2, AlertTriangle, Clock, Bell, Settings2, HardHat } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "./ui/sonner";

const money = (n) => "$" + Number(n || 0).toLocaleString();
const COLORS = { paid: "#16a34a", inReview: "#d97706", planned: "#cbd5e1" };

function StackBar({ paid, inReview, planned, contract }) {
  const tot = contract || paid + inReview + planned || 1;
  const seg = (v, c) => (v > 0 ? <div style={{ width: `${(v / tot) * 100}%`, background: c }} className="h-full" /> : null);
  return (
    <div className="flex h-2.5 w-full rounded-full overflow-hidden bg-slate-100">
      {seg(paid, COLORS.paid)}{seg(inReview, COLORS.inReview)}{seg(planned, COLORS.planned)}
    </div>
  );
}

export default function PaymentProgress() {
  const { lang, user, api } = useApp();
  const zh = lang === "zh";
  const canNudge = ["admin", "eng_manager", "ceo", "finance"].includes(user.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState({ series: [], trades: [] });
  const [trendKey, setTrendKey] = useState("total");
  const [overdue, setOverdue] = useState({ items: [], count: 0, highCount: 0 });
  const [cfg, setCfg] = useState({ reviewDays: 3, plannedDays: 30, canEdit: false });
  const [editCfg, setEditCfg] = useState(false);
  const [nudging, setNudging] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, tr, od, cf] = await Promise.all([
        api.get("/finance/subcontract-progress"),
        api.get("/finance/subcontract-payment-trend"),
        api.get("/finance/subcontract-overdue"),
        api.get("/finance/subcontract-overdue-config"),
      ]);
      setData(p.data); setTrend(tr.data || { series: [], trades: [] });
      setOverdue(od.data || { items: [], count: 0, highCount: 0 });
      setCfg(cf.data || { reviewDays: 3, plannedDays: 30, canEdit: false });
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async () => {
    try {
      const { data } = await api.put("/finance/subcontract-overdue-config", { reviewDays: Number(cfg.reviewDays), plannedDays: Number(cfg.plannedDays) });
      setCfg({ ...data }); setEditCfg(false);
      toast.success(zh ? "预警阈值已更新" : "Thresholds saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
  };

  const nudge = async (it, i) => {
    setNudging(`${it.subcontractId}-${i}`);
    try {
      const { data } = await api.post("/finance/payments/nudge", it);
      const roleLabel = { eng_manager: zh ? "工程经理" : "Construction Mgr", finance: zh ? "财务" : "Finance", cashier: zh ? "出纳" : "Cashier", ceo: zh ? "总经理" : "CEO" }[data.targetRole] || data.targetRole;
      toast.success(zh ? `已催付 ${roleLabel}（站内 ${data.notified}${data.smsConfigured ? ` · 短信 ${data.sms}` : "，短信未配置"}）` : `Nudged ${roleLabel} (${data.notified} in-app${data.smsConfigured ? `, ${data.sms} SMS` : ", SMS off"})`);
    } catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setNudging(null); }
  };

  if (loading) return <div className="flex justify-center py-16 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return <div className="text-center py-16 text-slate-400">{zh ? "暂无数据" : "No data"}</div>;
  const t = data.totals || {};

  const KPI = ({ label, val, color, tid }) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`pp-kpi-${tid}`}>
      <p className="text-xs text-slate-500">{label}</p>
      <p className="text-xl font-bold mt-1" style={{ color }}>{money(val)}</p>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="payment-progress">
      <div className={`rounded-xl border p-4 ${overdue.count > 0 ? "bg-red-50 border-red-200" : "bg-white border-slate-200"}`} data-testid="pp-overdue-panel">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className={`h-4 w-4 ${overdue.count > 0 ? "text-red-600" : "text-slate-400"}`} />
            <p className={`text-sm font-semibold ${overdue.count > 0 ? "text-red-700" : "text-slate-600"}`}>
              {overdue.count > 0
                ? (zh ? `超期未付预警 · ${overdue.count} 项` : `Overdue payments · ${overdue.count}`)
                : (zh ? "超期未付预警 · 暂无" : "Overdue payments · none")}
              {overdue.highCount > 0 && <span className="ml-2 text-[11px] bg-red-600 text-white px-1.5 py-0.5 rounded-full">{zh ? `严重 ${overdue.highCount}` : `High ${overdue.highCount}`}</span>}
            </p>
            {cfg.canEdit && (
              <button data-testid="pp-cfg-toggle" onClick={() => setEditCfg((v) => !v)} className="ml-auto text-slate-400 hover:text-slate-700" title={zh ? "设置预警阈值" : "Threshold settings"}>
                <Settings2 className="h-4 w-4" />
              </button>
            )}
          </div>
          {editCfg && cfg.canEdit ? (
            <div data-testid="pp-cfg-editor" className="flex items-end gap-2 flex-wrap mb-2 bg-white border border-slate-200 rounded-lg p-2.5">
              <div>
                <label className="text-[10px] text-slate-500 block mb-0.5">{zh ? "审批停留 ≥ (天)" : "In review ≥ (days)"}</label>
                <Input data-testid="pp-cfg-review" type="number" value={cfg.reviewDays} onChange={(e) => setCfg((c) => ({ ...c, reviewDays: e.target.value }))} className="h-8 w-24 text-sm" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-0.5">{zh ? "待发起 ≥ (天)" : "Planned ≥ (days)"}</label>
                <Input data-testid="pp-cfg-planned" type="number" value={cfg.plannedDays} onChange={(e) => setCfg((c) => ({ ...c, plannedDays: e.target.value }))} className="h-8 w-24 text-sm" />
              </div>
              <Button data-testid="pp-cfg-save" size="sm" className="h-8 bg-brand-500 hover:bg-brand-600 text-white text-xs" onClick={saveCfg}>{zh ? "保存" : "Save"}</Button>
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setEditCfg(false)}>{zh ? "取消" : "Cancel"}</Button>
            </div>
          ) : (
            <p className="text-[11px] text-slate-400 mb-2">{zh ? `审批停留 ≥ ${overdue.reviewDays} 天 或 合同 ≥ ${overdue.plannedDays} 天仍未发起付款` : `In review ≥ ${overdue.reviewDays}d, or planned ≥ ${overdue.plannedDays}d`}</p>
          )}
          {overdue.count > 0 && (
          <div className="space-y-1 max-h-52 overflow-y-auto">
            {overdue.items.slice(0, 15).map((it, i) => (
              <div key={i} data-testid={`pp-overdue-${it.subcontractId}-${i}`} className="flex items-center gap-2 text-xs bg-white rounded-md px-2.5 py-1.5 border border-red-100">
                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${it.severity === "high" ? "bg-red-600 text-white" : "bg-amber-100 text-amber-700"}`}>
                  {it.kind === "review" ? (zh ? "审批中" : "In review") : (zh ? "待发起" : "Planned")}
                </span>
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-slate-700">{it.tradeName}</span>
                  <span className="text-slate-400"> · {it.milestoneName}</span>
                  <span className="text-slate-300 block truncate text-[10px]">{it.address}</span>
                </div>
                <span className="text-slate-600 flex-shrink-0">{money(it.amount)}</span>
                <span className="flex items-center gap-0.5 text-red-600 font-semibold flex-shrink-0"><Clock className="h-3 w-3" />{it.days}{zh ? "天" : "d"}</span>
                {canNudge && (
                  <Button data-testid={`pp-nudge-${it.subcontractId}-${i}`} size="sm" className="h-6 px-2 text-[10px] bg-brand-500 hover:bg-brand-600 text-white flex-shrink-0"
                    disabled={nudging === `${it.subcontractId}-${i}`} onClick={() => nudge(it, i)}>
                    {nudging === `${it.subcontractId}-${i}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Bell className="h-3 w-3 mr-1" />{zh ? "催付" : "Nudge"}</>}
                  </Button>
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI tid="contract" label={zh ? "合同总额" : "Total"} val={t.contract} color="#0f172a" />
        <KPI tid="paid" label={zh ? "已支付" : "Paid"} val={t.paid} color={COLORS.paid} />
        <KPI tid="inReview" label={zh ? "审批中" : "In review"} val={t.inReview} color={COLORS.inReview} />
        <KPI tid="planned" label={zh ? "待支付" : "Pending"} val={t.planned} color="#64748b" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold text-slate-800">{zh ? "整体付款进度" : "Overall progress"}</p>
          <span className="text-sm font-bold" style={{ color: COLORS.paid }}>{t.pct}%</span>
        </div>
        <StackBar paid={t.paid} inReview={t.inReview} planned={t.planned} contract={t.contract} />
        <div className="flex gap-4 mt-2 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full inline-block" style={{ background: COLORS.paid }} />{zh ? "已支付" : "Paid"}</span>
          <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full inline-block" style={{ background: COLORS.inReview }} />{zh ? "审批中" : "In review"}</span>
          <span className="flex items-center gap-1"><i className="h-2 w-2 rounded-full inline-block" style={{ background: COLORS.planned }} />{zh ? "待支付" : "Pending"}</span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="pp-trend">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-red-600" />{zh ? "分包放款趋势（按月）" : "Disbursement Trend (monthly)"}</p>
          <Select value={trendKey} onValueChange={setTrendKey}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="pp-trend-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="total">{zh ? "全部工种" : "All trades"}</SelectItem>
              {(trend.trades || []).map((tr) => <SelectItem key={tr.tradeKey} value={tr.tradeKey}>{tr.tradeName}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(trend.series || []).length === 0 ? (
          <p className="text-slate-400 text-sm py-10 text-center">{zh ? "暂无放款记录" : "No disbursements yet"}</p>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend.series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => "$" + (v >= 1000 ? (v / 1000) + "k" : v)} />
              <Tooltip formatter={(v) => money(v)} />
              <Line type="monotone" dataKey={trendKey} stroke="#4F6BFF" strokeWidth={2.5} dot={{ r: 3 }} name={zh ? "放款额" : "Paid"} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-red-600" />{zh ? "按工种汇总" : "By Trade"}</p>
          <div className="space-y-3">
            {(data.byTrade || []).map((r) => (
              <div key={r.tradeKey} data-testid={`pp-trade-${r.tradeKey}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-medium text-slate-700">{r.tradeName} <span className="text-slate-400">· {r.count}</span></span>
                  <span className="text-slate-500">{money(r.paid)} / {money(r.contract)} <b style={{ color: COLORS.paid }}>{r.pct}%</b></span>
                </div>
                <StackBar paid={r.paid} inReview={r.inReview} planned={r.planned} contract={r.contract} />
              </div>
            ))}
            {(!data.byTrade || data.byTrade.length === 0) && <p className="text-slate-400 text-sm py-6 text-center">{zh ? "暂无分包合同" : "No subcontracts"}</p>}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="pp-compare">
          <p className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-1.5"><HardHat className="h-4 w-4 text-red-600" />{zh ? "付款 vs 施工进度对照" : "Pay vs Construction Progress"}</p>
          <p className="text-[11px] text-slate-400 mb-3">{zh ? "已付比例明显高于施工进度 = 超付风险" : "Paid % well above construction % = overpay risk"}</p>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {(data.byProject || []).map((r) => (
              <div key={r.projectId} data-testid={`pp-project-${r.projectId}`}>
                <div className="flex items-center justify-between text-xs mb-1 gap-2">
                  <span className="font-medium text-slate-700 truncate">{r.address || r.projectId}</span>
                  {r.overpayRisk && <span data-testid={`pp-overpay-${r.projectId}`} className="text-[9px] font-semibold bg-red-600 text-white px-1.5 py-0.5 rounded-full flex-shrink-0">{zh ? "超付风险" : "Overpay risk"}</span>}
                </div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-[10px] text-slate-400 w-14 flex-shrink-0">{zh ? "施工" : "Build"}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full" style={{ width: `${r.progressPct}%`, background: "#2563eb" }} /></div>
                  <span className="text-[10px] text-slate-500 w-9 text-right flex-shrink-0">{r.progressPct}%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400 w-14 flex-shrink-0">{zh ? "已付" : "Paid"}</span>
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full" style={{ width: `${r.pct}%`, background: r.overpayRisk ? "#dc2626" : COLORS.paid }} /></div>
                  <span className="text-[10px] text-slate-500 w-9 text-right flex-shrink-0">{r.pct}%</span>
                </div>
              </div>
            ))}
            {(!data.byProject || data.byProject.length === 0) && <p className="text-slate-400 text-sm py-6 text-center">{zh ? "暂无数据" : "No data"}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
